import { parseAbi, encodeFunctionData, zeroAddress } from "viem";
import { NATIVE } from "./swap";
import { withSlippage, MAX_U128 } from "./v3math";

export const npmAbi = parseAbi([
  "struct MintParams { address token0; address token1; uint24 fee; int24 tickLower; int24 tickUpper; uint256 amount0Desired; uint256 amount1Desired; uint256 amount0Min; uint256 amount1Min; address recipient; uint256 deadline; }",
  "struct IncreaseLiquidityParams { uint256 tokenId; uint256 amount0Desired; uint256 amount1Desired; uint256 amount0Min; uint256 amount1Min; uint256 deadline; }",
  "struct DecreaseLiquidityParams { uint256 tokenId; uint128 liquidity; uint256 amount0Min; uint256 amount1Min; uint256 deadline; }",
  "struct CollectParams { uint256 tokenId; address recipient; uint128 amount0Max; uint128 amount1Max; }",
  "function createAndInitializePoolIfNecessary(address token0, address token1, uint24 fee, uint160 sqrtPriceX96) payable returns (address pool)",
  "function mint(MintParams params) payable returns (uint256 tokenId, uint128 liquidity, uint256 amount0, uint256 amount1)",
  "function increaseLiquidity(IncreaseLiquidityParams params) payable returns (uint128 liquidity, uint256 amount0, uint256 amount1)",
  "function decreaseLiquidity(DecreaseLiquidityParams params) payable returns (uint256 amount0, uint256 amount1)",
  "function collect(CollectParams params) payable returns (uint256 amount0, uint256 amount1)",
  "function multicall(bytes[] data) payable returns (bytes[] results)",
  "function refundETH() payable",
  "function burn(uint256 tokenId) payable",
  "function unwrapWETH9(uint256 amountMinimum, address recipient) payable",
  "function sweepToken(address token, uint256 amountMinimum, address recipient) payable",
  "function balanceOf(address owner) view returns (uint256)",
  "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
  "function positions(uint256 tokenId) view returns (uint96 nonce, address operator, address token0, address token1, uint24 fee, int24 tickLower, int24 tickUpper, uint128 liquidity, uint256 feeGrowthInside0LastX128, uint256 feeGrowthInside1LastX128, uint128 tokensOwed0, uint128 tokensOwed1)",
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
  "event IncreaseLiquidity(uint256 indexed tokenId, uint128 liquidity, uint256 amount0, uint256 amount1)",
]);

export const factoryV3Abi = parseAbi(["function getPool(address,address,uint24) view returns (address)"]);
export const poolV3Abi = parseAbi([
  "function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)",
  "function liquidity() view returns (uint128)",
]);

const enc = (functionName, args) => encodeFunctionData({ abi: npmAbi, functionName, args });
export const deadline = (min = 20) => BigInt(Math.floor(Date.now() / 1000) + min * 60);
export const lpAddr = (t, cfg) => (t.address === NATIVE ? cfg.contracts.WETH9 : t.address);

// Builds NPM multicall for mint (optionally creating + initialising the pool in the same tx).
export function buildMint({ t0, t1, fee, tickLower, tickUpper, a0, a1, slipBps, account, create, sqrtPriceX96, nativeIs0, nativeIs1 }) {
  const calls = [];
  if (create) calls.push(enc("createAndInitializePoolIfNecessary", [t0, t1, fee, sqrtPriceX96]));
  calls.push(enc("mint", [{ token0: t0, token1: t1, fee, tickLower, tickUpper, amount0Desired: a0, amount1Desired: a1,
    amount0Min: withSlippage(a0, slipBps), amount1Min: withSlippage(a1, slipBps), recipient: account, deadline: deadline() }]));
  const value = nativeIs0 ? a0 : nativeIs1 ? a1 : 0n;
  if (value > 0n) calls.push(enc("refundETH", []));
  return { calls, value };
}

export function buildIncrease({ tokenId, a0, a1, slipBps, nativeIs0, nativeIs1 }) {
  const calls = [enc("increaseLiquidity", [{ tokenId, amount0Desired: a0, amount1Desired: a1, amount0Min: withSlippage(a0, slipBps), amount1Min: withSlippage(a1, slipBps), deadline: deadline() }])];
  const value = nativeIs0 ? a0 : nativeIs1 ? a1 : 0n;
  if (value > 0n) calls.push(enc("refundETH", []));
  return { calls, value };
}

// Remove liquidity and collect (principal + fees). With unwrap, WETH is returned as native ETH.
// burn: appends NPM.burn(tokenId) — only valid when the whole liquidity is removed and everything owed is collected in the same tx.
// This destroys the empty LP position NFT; it never touches the underlying tokens.
export function buildRemove({ tokenId, liquidity, e0, e1, slipBps, account, unwrap, t0, t1, weth, burn }) {
  const m0 = withSlippage(e0, slipBps);
  const m1 = withSlippage(e1, slipBps);
  const calls = liquidity > 0n ? [enc("decreaseLiquidity", [{ tokenId, liquidity, amount0Min: m0, amount1Min: m1, deadline: deadline() }])] : [];
  const wethIs0 = t0.toLowerCase() === weth.toLowerCase();
  const wethIs1 = t1.toLowerCase() === weth.toLowerCase();
  if (unwrap && (wethIs0 || wethIs1)) {
    calls.push(enc("collect", [{ tokenId, recipient: zeroAddress, amount0Max: MAX_U128, amount1Max: MAX_U128 }]));
    calls.push(enc("unwrapWETH9", [wethIs0 ? m0 : m1, account]));
    calls.push(enc("sweepToken", [wethIs0 ? t1 : t0, wethIs0 ? m1 : m0, account]));
  } else {
    calls.push(enc("collect", [{ tokenId, recipient: account, amount0Max: MAX_U128, amount1Max: MAX_U128 }]));
  }
  if (burn) calls.push(enc("burn", [tokenId]));
  return { calls, value: 0n };
}
